import { useMemo } from 'react';
import dayjs from 'dayjs';
import { Card, DatePicker, Table, Tag } from 'antd';
import { PageShell, RefreshButton } from '../components/page-shell.js';
import { microUsd } from './dashboard-page.js';
import type { FinanceResponse } from '../types.js';
import { useAdminQuery } from '../use-admin-query.js';

export function FinancePage() {
  const path = useMemo(() => {
    const to = dayjs().format('YYYY-MM-DD');
    const from = dayjs().subtract(30, 'day').format('YYYY-MM-DD');
    return `/admin/v1/finance?from=${from}&to=${to}`;
  }, []);
  const query = useAdminQuery<FinanceResponse>(path);
  return (
    <PageShell
      title="AI finance"
      description="Effective-dated provider cost by product, provider, model, operation, plan, and UTC day. This is not product revenue accounting."
      action={
        <RefreshButton
          loading={query.loading}
          onClick={() => void query.refresh()}
        />
      }
      error={query.error}
    >
      <Card
        title="Daily provider cost · last 30 days"
        extra={
          <DatePicker.RangePicker
            disabled
            value={[dayjs().subtract(30, 'day'), dayjs()]}
          />
        }
      >
        <Table
          rowKey="id"
          loading={query.loading}
          dataSource={query.data?.rows ?? []}
          scroll={{ x: 1100 }}
          summary={(rows) => (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={6}>
                Total visible provider cost
              </Table.Summary.Cell>
              <Table.Summary.Cell index={1}>
                <strong>
                  {microUsd(
                    rows
                      .reduce((sum, row) => sum + BigInt(row.costMicrousd), 0n)
                      .toString(),
                  )}
                </strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={2} />
            </Table.Summary.Row>
          )}
          columns={[
            {
              title: 'Day',
              dataIndex: 'day',
              render: (value: string) => value.slice(0, 10),
            },
            {
              title: 'Product',
              render: (_, row) => <Tag>{row.product.key}</Tag>,
            },
            { title: 'Provider', dataIndex: 'provider' },
            { title: 'Model', dataIndex: 'model' },
            { title: 'Operation', dataIndex: 'operationKey' },
            { title: 'Plan', dataIndex: 'planKey' },
            { title: 'Cost', dataIndex: 'costMicrousd', render: microUsd },
            { title: 'Calls', dataIndex: 'requestCount' },
          ]}
        />
      </Card>
    </PageShell>
  );
}
